import { Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { DownloadIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FileDownloadFormat } from '@/shared/lib';
import { DownloadFormatBadges, IconButton, TruncatedText } from '@/shared/ui';
import type { DownloadTemplateItem } from '../../model/api/types';

type Props = {
  /** Название действия: и доступное имя кнопки, и её подсказка — с указанием, что скачивается */
  label: string;
  /** Шаблоны сущности в порядке, в котором их отдаёт бэкенд */
  templates: DownloadTemplateItem[];
  /** Скачивание по выбранному шаблону в выбранном формате */
  onSelect: (template: DownloadTemplateItem, format: FileDownloadFormat) => void;
  /** Файл формируется — повторное скачивание не запускается */
  isPending: boolean;
  /** Классы кнопки-триггера */
  className?: string;
};

/**
 * Кнопка скачивания файлом: иконка загрузки, по клику — шаблоны сущности по их наименованиям.
 * Рядом с наименованием стоят лейблы форматов, и скачивание запускает выбор лейбла: формат
 * обязателен, поэтому у самой строки шаблона действия нет. Список раскрывается всплывающим
 * окном, а не меню: три равноправных действия в одной строке меню обходились бы только Tab'ом.
 *
 * Название действия приходит от места применения: один и тот же значок скачивает то транскрипцию,
 * то заключение, и «Скачать» на кнопке не говорит, что именно будет скачано.
 */
export const DownloadByTemplateMenu: FC<Props> = ({ label, templates, onSelect, isPending, className }) => {
  const { t } = useTranslation(['dialogs']);
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (template: DownloadTemplateItem, format: FileDownloadFormat) => {
    setIsOpen(false);
    onSelect(template, format);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <IconButton
        size="icon"
        className={className}
        label={label}
        disabled={isPending}
        icon={<DownloadIcon className="size-4" />}
        trigger={PopoverTrigger}
      />
      <PopoverContent align="end" className="flex w-80 flex-col gap-2 p-2">
        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dialogs:downloadTemplate.empty')}</p>
        ) : (
          templates.map((template) => (
            <div
              key={template.id}
              className="flex items-center gap-3"
              data-testid={`download-template-option-${template.id}`}
            >
              <TruncatedText className="text-sm">{template.name}</TruncatedText>
              <DownloadFormatBadges
                className="ml-auto"
                onSelect={(format) => handleSelect(template, format)}
                getAriaLabel={(format) => t('dialogs:downloadTemplate.asFormat', { name: template.name, format })}
              />
            </div>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};
